<template>
  <div>
    <CButton color="secondary" size="sm" class="mb-3" @click="$router.back()">
      <CIcon name="cil-arrow-left" /> Back
    </CButton>

    <CCard v-if="loading">
      <CCardBody class="text-center py-5">
        <CSpinner color="primary" />
        <p class="mt-3">Generating SST-03 report...</p>
      </CCardBody>
    </CCard>

    <CAlert v-else-if="error" color="danger">
      <h4>Error</h4>
      <p>{{ error }}</p>
    </CAlert>

    <template v-else-if="report">
      <!-- Header -->
      <CCard>
        <CCardHeader class="bg-primary text-white">
          <h3 class="mb-0">Malaysian SST-03 Return Form</h3>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="6">
              <h5>Company Information</h5>
              <dl class="row">
                <dt class="col-sm-4">Company Name:</dt>
                <dd class="col-sm-8">{{ report.companyInfo.name }}</dd>
                <dt class="col-sm-4">SST No:</dt>
                <dd class="col-sm-8">{{ report.companyInfo.sstRegistrationNo }}</dd>
              </dl>
            </CCol>
            <CCol md="6">
              <h5>Period Information</h5>
              <dl class="row">
                <dt class="col-sm-4">Period:</dt>
                <dd class="col-sm-8">{{ report.periodInfo.periodCode }}</dd>
                <dt class="col-sm-4">Due Date:</dt>
                <dd class="col-sm-8">{{ formatDate(report.periodInfo.dueDate) }}</dd>
              </dl>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <!-- Part A: Output Tax -->
      <CCard>
        <CCardHeader class="bg-danger text-white">
          <strong>PART A: Taxable Sales (Output Tax)</strong>
        </CCardHeader>
        <CCardBody>
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Description</th>
                <th class="text-right">Taxable Sales (RM)</th>
                <th class="text-right">Tax Amount (RM)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in outputTaxByRate" :key="item.taxRate">
                <td>Standard Rated Sales @ {{ item.taxRate }}%</td>
                <td class="text-right">{{ formatCurrency(item.taxableSales) }}</td>
                <td class="text-right">{{ formatCurrency(item.taxAmount) }}</td>
              </tr>
              <tr class="table-active">
                <td><strong>Total Output Tax</strong></td>
                <td></td>
                <td class="text-right"><strong>{{ formatCurrency(report.partA_TaxableSales.totalOutputTax) }}</strong></td>
              </tr>
            </tbody>
          </table>
        </CCardBody>
      </CCard>

      <!-- Part B: Input Tax -->
      <CCard>
        <CCardHeader class="bg-success text-white">
          <strong>PART B: Taxable Purchases (Input Tax)</strong>
        </CCardHeader>
        <CCardBody>
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Description</th>
                <th class="text-right">Taxable Purchases (RM)</th>
                <th class="text-right">Tax Amount (RM)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in inputTaxByRate" :key="item.taxRate">
                <td>
                  Standard Rated Purchases @ {{ item.taxRate }}%
                  <span v-if="!inputTaxClaimable" class="text-muted small ml-1">(not claimable)</span>
                </td>
                <td class="text-right">{{ formatCurrency(item.taxablePurchases) }}</td>
                <td class="text-right" :class="{ 'text-muted': !inputTaxClaimable }">
                  {{ formatCurrency(item.taxAmount) }}
                </td>
              </tr>
              <tr class="table-active">
                <td>
                  <strong v-if="inputTaxClaimable">Total input tax (claimable)</strong>
                  <template v-else>
                    <strong>SST paid on purchases</strong>
                    <CBadge color="secondary" class="ml-1">Not claimable</CBadge>
                    <CIcon
                      name="cil-info"
                      size="sm"
                      class="text-muted ml-1"
                      tabindex="0"
                      :aria-label="notClaimableHint"
                      v-c-tooltip="{ content: notClaimableHint, placement: 'top' }"
                    />
                  </template>
                </td>
                <td></td>
                <td class="text-right" :class="{ 'text-muted': !inputTaxClaimable }">
                  <strong>{{ formatCurrency(partBInputTaxPaid) }}</strong>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-if="!inputTaxClaimable" class="text-muted small mb-0">{{ notClaimableHint }}</p>
          <p v-else-if="report.partB_TaxablePurchases.note" class="text-muted small mb-0">
            {{ report.partB_TaxablePurchases.note }}
          </p>
        </CCardBody>
      </CCard>

      <!-- Part C: Tax Calculation -->
      <CCard>
        <CCardHeader class="bg-primary text-white">
          <strong>PART C: Tax Calculation & Payment</strong>
        </CCardHeader>
        <CCardBody>
          <table class="table table-bordered">
            <tbody>
              <tr>
                <td>Total Output Tax (Part A)</td>
                <td class="text-right">{{ formatCurrency(report.partC_TaxCalculation.totalOutputTax) }}</td>
              </tr>
              <tr v-if="partCDeductsInputTax">
                <td>Less: input tax (claimable, Part B)</td>
                <td class="text-right">({{ formatCurrency(partCInputTaxDeducted) }})</td>
              </tr>
              <tr v-else>
                <td class="text-muted">
                  Input tax (Part B)
                  <CIcon
                    name="cil-info"
                    size="sm"
                    class="ml-1"
                    tabindex="0"
                    :aria-label="notClaimableHint"
                    v-c-tooltip="{ content: notClaimableHint, placement: 'top' }"
                  />
                </td>
                <td class="text-right text-muted">Not claimable</td>
              </tr>
              <tr class="table-active">
                <td><strong>Net Tax Payable / (Refundable)</strong></td>
                <td class="text-right">
                  <strong :class="report.partC_TaxCalculation.netTaxPayable < 0 ? 'text-success' : 'text-danger'">
                    {{ formatCurrency(Math.abs(report.partC_TaxCalculation.netTaxPayable)) }}
                    <span v-if="report.partC_TaxCalculation.netTaxPayable < 0">(Refundable)</span>
                  </strong>
                </td>
              </tr>
              <tr>
                <td><strong>Total Tax Due</strong></td>
                <td class="text-right"><strong>{{ formatCurrency(report.partC_TaxCalculation.totalTaxDue) }}</strong></td>
              </tr>
            </tbody>
          </table>
        </CCardBody>
      </CCard>

      <!-- Actions -->
      <CCard>
        <CCardBody class="text-center">
          <CButton color="primary" size="lg" @click="printReport">
            <CIcon name="cil-print" /> Print Report
          </CButton>
          <CButton color="info" size="lg" class="ml-2" disabled>
            <CIcon name="cil-cloud-download" /> Download PDF (Coming Soon)
          </CButton>
        </CCardBody>
      </CCard>

      <!-- Disclaimer -->
      <CAlert color="warning" show>
        <h5 class="alert-heading">
          <CIcon name="cil-warning" /> Important Notice
        </h5>
        <p class="mb-2">
          This is an <strong>internal summary report</strong> generated for your reference only.
          It is designed to help you review your SST transactions before official filing.
        </p>
        <hr>
        <p class="mb-0">
          <strong>For official SST return filing:</strong> Please submit your SST-02 form through the
          <a href="https://mysst.customs.gov.my" target="_blank" rel="noopener" class="alert-link">
            Royal Malaysian Customs Department (RMCD) MySST portal
          </a>.
        </p>
      </CAlert>
    </template>
  </div>
</template>

<script>
import TaxAPI from '@/lib/taxApi'
import { formatMyr } from '@/utils/money'

export default {
  name: 'SST03Report',
  data() {
    return {
      loading: false,
      error: null,
      report: null,
      notClaimableHint: "SST paid on purchases is part of your cost and can't be deducted. Ask your tax agent if your business qualifies, then change it in Business settings."
    }
  },
  computed: {
    // Per-rate rows come from transactionSummary (Part A/B only carry the 8% / 6% / other totals)
    outputTaxByRate() {
      return this.report?.transactionSummary?.outputTaxByRate || []
    },
    inputTaxByRate() {
      return this.report?.transactionSummary?.inputTaxByRate || []
    },
    inputTaxClaimable() {
      return !!this.report?.partB_TaxablePurchases?.inputTaxClaimable
    },
    // Part C shows the API's numbers. A period not yet recalculated after the setting changed can
    // still carry a deduction; show it as such rather than hiding an amount the net includes.
    partCDeductsInputTax() {
      const partC = this.report?.partC_TaxCalculation
      return !!partC && (partC.inputTaxClaimable || Number(this.partCInputTaxDeducted) !== 0)
    },
    // Unambiguous API names (old names kept by the API with the same values, used as fallback):
    // Part B inputTaxPaid = all SST paid on purchases; Part C inputTaxDeducted = claimable part only.
    partBInputTaxPaid() {
      const partB = this.report?.partB_TaxablePurchases
      return partB ? (partB.inputTaxPaid ?? partB.totalInputTax) : 0
    },
    partCInputTaxDeducted() {
      const partC = this.report?.partC_TaxCalculation
      return partC ? (partC.inputTaxDeducted ?? partC.totalInputTax) : 0
    }
  },
  async mounted() {
    this.$store.commit("setPageNavItems", ["Tax Management", "SST-03 Reports"])
    await this.loadReport()
  },
  methods: {
    async loadReport() {
      this.loading = true
      this.error = null

      try {
        const taxApi = new TaxAPI(window.localStorage.getItem('token'))
        const periodId = this.$route.params.periodId
        const response = await taxApi.getSST03Report(periodId)
        this.report = response.data.result
      } catch (error) {
        console.error('Error loading SST-03 report:', error)
        this.error = error.response?.data?.message || 'Failed to load report'
      } finally {
        this.loading = false
      }
    },

    printReport() {
      window.print()
    },

    formatCurrency(amount) {
      return `RM ${formatMyr(amount)}`
    },

    formatDate(date) {
      const taxApi = new TaxAPI()
      return taxApi.formatDate(date)
    }
  }
}
</script>

<style scoped>
@media print {
  .btn, .card-header { display: none; }
}
</style>